import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import {
  ArrowLeft,
  Image,
  Film,
  Type,
  Mic,
  Eye,
  Clock,
  MessageCircle,
  CheckCircle2,
  Loader2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VoiceNotePlayer } from '@/components/VoiceNotePlayer';
import { useAuth } from '@/hooks/useAuth';
import { getApiBaseUrl } from '@/lib/capacitor';
import { formatDate } from '@/lib/utils';

interface Post {
  _id: string;
  state: string;
  contentType: 'photo' | 'video' | 'hudText' | 'voiceNote';
  caption?: string;
  hudText?: string;
  originalFilename?: string;
  processedFilename?: string;
  voiceNoteFilename?: string;
  viewCount: number;
  user: {
    _id: string;
    name: string;
    email?: string;
  };
  createdAt: string;
  lastReadAt?: string;
  lastPublishedAt?: string;
}

interface Reaction {
  _id: string;
  post: string;
  author?: {
    _id: string;
    name: string;
    email?: string;
  };
  text: string;
  read: boolean;
  createdAt: string;
}

const contentTypeIcons = {
  photo: Image,
  video: Film,
  hudText: Type,
  voiceNote: Mic,
};

const stateColors: Record<string, string> = {
  uploading: 'bg-gray-500',
  uploaded: 'bg-gray-500',
  'waiting-for-processing': 'bg-purple-500',
  processing: 'bg-purple-500 animate-pulse',
  processed: 'bg-blue-500',
  unread: 'bg-amber-500',
  published: 'bg-emerald-500',
  read: 'bg-blue-500',
  archived: 'bg-gray-500',
  'processing-failed': 'bg-red-500',
  'publish-failed': 'bg-red-500',
};

export default function PostDetailPage() {
  const navigate = useNavigate();
  const { postId } = useParams<{ postId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Scroll to top when navigating to this page
  useScrollToTop();
  const highlightReactionId = searchParams.get('reaction');
  const showProcessedBanner = searchParams.get('processed') === 'true';
  
  const { user, isLoading: authLoading, authenticatedFetch } = useAuth();
  const [post, setPost] = useState<Post | null>(null);
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [processingProgress, setProcessingProgress] = useState<{ percent: number; time?: string } | null>(null);
  
  const reactionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const videoRef = useRef<HTMLVideoElement>(null);

  // Handle processed=true query param - show banner and auto-dismiss
  useEffect(() => {
    if (showProcessedBanner) {
      setShowSuccessBanner(true);
      
      // Remove the query param from URL (keep it clean)
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('processed');
      setSearchParams(newParams, { replace: true });
      
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => {
        setShowSuccessBanner(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [showProcessedBanner, searchParams, setSearchParams]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/share');
      return;
    }

    if (user && postId) {
      fetchPost();
      fetchReactions();
    }
  }, [user, authLoading, postId, navigate]);

  // Listen for processing progress events to update UI
  useEffect(() => {
    const handleProcessingProgress = (event: Event) => {
      const customEvent = event as CustomEvent<{ postId: string; percent: number; time?: string }>;
      // Only update if this is the post we're viewing
      if (customEvent.detail.postId === postId) {
        setProcessingProgress({
          percent: customEvent.detail.percent,
          time: customEvent.detail.time,
        });
      }
    };

    window.addEventListener('ping-processing-progress', handleProcessingProgress);
    return () => {
      window.removeEventListener('ping-processing-progress', handleProcessingProgress);
    };
  }, [postId]);

  // Listen for processing complete events to refresh the post
  useEffect(() => {
    const handleProcessingComplete = (event: Event) => {
      const customEvent = event as CustomEvent<{ postId: string; success: boolean }>;
      // Only refresh if this is the post we're viewing
      if (customEvent.detail.postId === postId) {
        console.log('[PostDetailPage] Processing complete for this post, refreshing');
        setProcessingProgress(null); // Clear progress
        fetchPost();
        
        // Show the success banner if processing succeeded
        if (customEvent.detail.success) {
          setShowSuccessBanner(true);
          // Auto-dismiss after 5 seconds
          setTimeout(() => {
            setShowSuccessBanner(false);
          }, 5000);
        }
      }
    };

    window.addEventListener('ping-processing-complete', handleProcessingComplete);
    return () => {
      window.removeEventListener('ping-processing-complete', handleProcessingComplete);
    };
  }, [postId]);

  // Scroll to and highlight reaction when specified
  useEffect(() => {
    if (highlightReactionId && reactions.length > 0) {
      const reactionElement = reactionRefs.current.get(highlightReactionId);
      if (reactionElement) {
        // Scroll into view with some delay to ensure layout is complete
        setTimeout(() => {
          reactionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        
        // Mark the reaction as read
        markReactionAsRead(highlightReactionId);
      }
    }
  }, [highlightReactionId, reactions]);

  const fetchPost = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      
      const response = user?.authType === 'google'
        ? await authenticatedFetch(`${baseUrl}/api/posts/${postId}`)
        : await fetch(`${baseUrl}/api/posts/${postId}`);
      
      const data = await response.json();
      
      if (data.success) {
        setPost(data.post);
      } else {
        console.error('Failed to fetch post:', data.error);
        navigate('/my-pings');
      }
    } catch (error) {
      console.error('Failed to fetch post:', error);
      navigate('/my-pings');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReactions = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      
      const response = user?.authType === 'google'
        ? await authenticatedFetch(`${baseUrl}/api/reactions/post/${postId}`)
        : await fetch(`${baseUrl}/api/reactions/post/${postId}`);
      
      const data = await response.json();
      
      if (data.success) {
        setReactions(data.reactions);
      }
    } catch (error) {
      console.error('Failed to fetch reactions:', error);
    }
  };

  const markReactionAsRead = async (reactionId: string) => {
    try {
      const baseUrl = getApiBaseUrl();
      
      const response = user?.authType === 'google'
        ? await authenticatedFetch(`${baseUrl}/api/reactions/${reactionId}/read`, {
            method: 'PATCH',
          })
        : await fetch(`${baseUrl}/api/reactions/${reactionId}/read`, {
            method: 'PATCH',
          });
      
      if (response.ok) {
        // Update local state
        setReactions(prev => 
          prev.map(r => r._id === reactionId ? { ...r, read: true } : r)
        );
      }
    } catch (error) {
      console.error('Failed to mark reaction as read:', error);
    }
  };

  const getMediaUrl = (filename?: string) => {
    if (!filename) return null;
    const baseUrl = getApiBaseUrl();
    return `${baseUrl}/uploads/${filename}`;
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Post not found</p>
      </div>
    );
  }

  const Icon = contentTypeIcons[post.contentType];
  const mediaUrl = getMediaUrl(post.processedFilename || post.originalFilename);
  const voiceNoteUrl = getMediaUrl(post.voiceNoteFilename);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 glass border-b border-border">
        <div className="max-w-lg mx-auto px-4 h-16 flex items-center gap-4">
          <Link to="/my-pings">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="font-display font-semibold flex-1">Ping Details</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-lg mx-auto p-4 space-y-6">
        {/* Processing Complete Banner */}
        {showSuccessBanner && (
          <div className="relative rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-4 animate-in slide-in-from-top-2">
            <button
              onClick={() => setShowSuccessBanner(false)}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-emerald-500/20 transition-colors"
            >
              <X className="w-4 h-4 text-emerald-500" />
            </button>
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="font-medium text-emerald-400">Video Ready!</p>
                <p className="text-sm text-muted-foreground">
                  Your video has finished processing and is now live.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Processing In Progress Banner with Progress */}
        {post && (post.state === 'processing' || post.state === 'waiting-for-processing') && (
          <div className="rounded-xl bg-purple-500/10 border border-purple-500/30 p-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-purple-400">
                  {processingProgress 
                    ? `Processing Video... ${Math.round(processingProgress.percent)}%`
                    : 'Processing Video...'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {processingProgress?.time 
                    ? `Elapsed: ${processingProgress.time}`
                    : "Your video is being processed. You'll be notified when it's ready."}
                </p>
              </div>
            </div>
            {/* Progress bar */}
            {processingProgress && (
              <div className="mt-3 h-2 bg-purple-500/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-purple-500 transition-all duration-300 ease-out"
                  style={{ width: `${Math.min(100, processingProgress.percent)}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* Media Section */}
        <div className="rounded-xl overflow-hidden bg-muted">
          {post.contentType === 'photo' && mediaUrl && (
            <img
              src={mediaUrl}
              alt=""
              className="w-full max-h-[60vh] object-contain"
            />
          )}
          
          {post.contentType === 'video' && (
            <div className="relative">
              {(post.state === 'processing' || post.state === 'waiting-for-processing') ? (
                <div className="w-full min-h-[300px] flex flex-col items-center justify-center bg-purple-500/10 gap-4">
                  <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
                  </div>
                  <p className="text-purple-400 font-medium">
                    {processingProgress 
                      ? `Processing... ${Math.round(processingProgress.percent)}%`
                      : 'Processing video...'}
                  </p>
                  {/* Progress bar */}
                  {processingProgress && (
                    <div className="w-48 h-2 bg-purple-500/20 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-purple-500 transition-all duration-300 ease-out"
                        style={{ width: `${Math.min(100, processingProgress.percent)}%` }}
                      />
                    </div>
                  )}
                </div>
              ) : mediaUrl ? (
                <video
                  ref={videoRef}
                  src={mediaUrl}
                  className="w-full max-h-[60vh] object-contain"
                  onClick={handleVideoClick}
                  onPlay={() => setIsVideoPlaying(true)}
                  onPause={() => setIsVideoPlaying(false)}
                  onEnded={() => setIsVideoPlaying(false)}
                  controls
                  playsInline
                />
              ) : (
                <div className="w-full min-h-[200px] flex items-center justify-center">
                  <Film className="w-16 h-16 text-muted-foreground" />
                </div>
              )}
            </div>
          )}
          
          {post.contentType === 'hudText' && (
            <div className="p-8 flex items-center justify-center min-h-[200px]">
              <p className="text-2xl font-medium text-center">{post.hudText}</p>
            </div>
          )}
          
          {post.contentType === 'voiceNote' && voiceNoteUrl && (
            <div className="p-8 flex flex-col items-center justify-center min-h-[200px] space-y-4">
              <Icon className="w-16 h-16 text-muted-foreground" />
              <VoiceNotePlayer src={voiceNoteUrl} />
            </div>
          )}
        </div>

        {/* Caption/Text */}
        {post.caption && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Caption</p>
            <p className="text-foreground">{post.caption}</p>
          </div>
        )}

        {/* Voice Note (for photo/hudText with attached voice) */}
        {voiceNoteUrl && post.contentType !== 'voiceNote' && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Voice Note</p>
            <VoiceNotePlayer src={voiceNoteUrl} />
          </div>
        )}

        {/* Metadata */}
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          {/* State */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Status</span>
            <div className="flex items-center gap-2">
              <div
                className={`w-2 h-2 rounded-full ${
                  stateColors[post.state] || 'bg-muted'
                }`}
              />
              <span className="text-sm capitalize">
                {post.state.replace('-', ' ')}
              </span>
            </div>
          </div>

          {/* Created */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Sent</span>
            <span className="text-sm flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              {formatDate(post.createdAt)}
            </span>
          </div>

          {/* View Count */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Views</span>
            <span className="text-sm flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" />
              {post.viewCount}
            </span>
          </div>

          {/* Last Published */}
          {post.lastPublishedAt && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Last shown</span>
              <span className="text-sm">{formatDate(post.lastPublishedAt)}</span>
            </div>
          )}
        </div>

        {/* Reactions Section */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-primary" />
            <h2 className="font-semibold">Reactions</h2>
            {reactions.length > 0 && (
              <span className="text-sm text-muted-foreground">
                ({reactions.length})
              </span>
            )}
          </div>

          {reactions.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-6 text-center">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No reactions yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {reactions.map((reaction) => {
                const isHighlighted = reaction._id === highlightReactionId;
                const isUnread = !reaction.read;
                
                return (
                  <div
                    key={reaction._id}
                    ref={(el) => {
                      if (el) {
                        reactionRefs.current.set(reaction._id, el);
                      }
                    }}
                    className={`
                      rounded-xl border bg-card p-4 transition-all duration-500
                      ${isHighlighted ? 'border-primary ring-2 ring-primary/20 reaction-highlight' : 'border-border'}
                      ${isUnread ? 'bg-primary/5' : ''}
                    `}
                    onClick={() => {
                      if (isUnread) {
                        markReactionAsRead(reaction._id);
                      }
                    }}
                  >
                    {reaction.author && (
                      <p className="text-sm font-medium text-primary mb-1">
                        {reaction.author.name}
                      </p>
                    )}
                    <p className="text-foreground whitespace-pre-wrap break-words">
                      {reaction.text}
                    </p>
                    <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      {formatDate(reaction.createdAt)}
                      {isUnread && (
                        <span className="ml-auto px-2 py-0.5 rounded-full bg-primary/20 text-primary text-xs font-medium">
                          New
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
